import { useState } from 'react';
import { StatusGlyph } from '../board/StatusGlyph';
import { StatusPicker } from '../board/StatusPicker';
import { parseArc } from '../plan/arc';
import type { Status } from '../plan/arc';
import type { HolderKind, Item } from './membership';

export interface Draft {
  name: string;
  items: Item[];
  status: Status;
}

/**
 * A new arc or nook before it exists: its name, what it will hold, and for an
 * arc where it starts. Nothing is written until Create.
 */
export function NewHolderDraft({
  kind,
  initialName,
  items,
  nameTaken,
  onCreate,
  onCancel,
}: {
  kind: HolderKind;
  initialName: string;
  items: readonly Item[];
  /** True for a name an arc already has; its file would be overwritten. */
  nameTaken: (name: string) => boolean;
  onCreate: (draft: Draft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [held, setHeld] = useState<Item[]>([...items]);
  const [status, setStatus] = useState<Status>('next');
  const [picking, setPicking] = useState<DOMRect | null>(null);

  const taken = kind === 'arc' && nameTaken(name);
  const canCreate = name.trim() !== '' && !taken;
  const create = () => canCreate && onCreate({ name: name.trim(), items: held, status });

  // Declares evidence so the picker does not ask for it: a draft has none to give.
  const standIn = parseArc('arcs/draft.md', `---\nstatus: ${status}\nevidence: draft\n---\n# draft\n`);

  return (
    <div className="palette-scrim">
      <div
        className="palette"
        role="dialog"
        aria-label={`New ${kind}`}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !picking) onCancel();
        }}
      >
        <div className="palette-field">
          <input
            autoFocus
            aria-label="Name"
            value={name}
            placeholder={`Name the ${kind}`}
            onChange={(e) => setName(e.target.value.replace(/\n/g, ' '))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                create();
              }
            }}
          />
        </div>

        <div className="palette-body">
          {taken && <p className="palette-none">An arc already has this name.</p>}

          {kind === 'arc' && (
            <section>
              <h2 className="palette-register">Starts</h2>
              <button
                type="button"
                className="palette-row"
                aria-label="Status"
                onClick={(e) => setPicking(e.currentTarget.getBoundingClientRect())}
              >
                <StatusGlyph status={status} />
                <span className="palette-label">{status}</span>
              </button>
            </section>
          )}

          <section>
            <h2 className="palette-register">Holds</h2>
            {held.length === 0 && <p className="palette-none">Nothing yet.</p>}
            {held.map((item) => (
              <div key={`${item.path}#${item.anchor ?? ''}`} className="palette-row">
                <span className="palette-label">{item.label}</span>
                <span className="palette-meta">{item.kind}</span>
                <button
                  type="button"
                  className="palette-take-out"
                  aria-label={`Take out ${item.label}`}
                  onClick={() => setHeld((h) => h.filter((x) => x !== item))}
                >
                  ✕
                </button>
              </div>
            ))}
          </section>
        </div>

        <div className="palette-foot palette-draft-foot">
          <button type="button" className="icon-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={!canCreate} onClick={create}>
            Create {kind}
          </button>
        </div>

        {picking && (
          <StatusPicker
            arc={standIn}
            anchor={picking}
            onClose={() => setPicking(null)}
            onPick={(next) => {
              setStatus(next);
              setPicking(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
